import React, { useState, useEffect } from 'react';
import { Button, TextField, InputAdornment, Snackbar, CircularProgress, Pagination, FormControl, Select, MenuItem, InputLabel } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { FaPlus } from 'react-icons/fa';
import AddPatientModal from './AddPatientModal';
import PatientsTable from './PatientsTable';
import DeletePatientModal from './DeletePatientModal';
import Sidebar from "../../components/Sidebar";
import TopNavbar from "../../components/TopNavbar";
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { getPatients as fetchPatientsApi } from '../../services/patientService';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Patients = () => {
    const [patients, setPatients] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPatient, setCurrentPatient] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [loading, setLoading] = useState(true); // Loading state
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [searchTypingTimer, setSearchTypingTimer] = useState(null);

    useEffect(() => {
        const fetchPatients = async () => {
            setLoading(true);
            const response = await fetchPatientsApi(currentPage, perPage, searchQuery);
            if (response.success && response.data.status === 'success') {
                setPatients(response.data.patients);
                setTotalPages(response.data.meta.total_pages);
                setTotal(response.data.meta.total);
            } else if (response?.data?.message) {
                console.error(response.data.message);
            }
            setLoading(false);
        };
        fetchPatients();
    }, [currentPage, perPage, searchQuery]);

    const handleAddPatient = () => {
        setCurrentPatient(null);
        setIsModalOpen(true);
    };

    const handleSubmitPatient = async () => {
        // Refresh current page after add/edit
        const response = await fetchPatientsApi(currentPage, perPage, searchQuery);
        if (response.success && response.data.status === 'success') {
            setPatients(response.data.patients);
            setTotalPages(response.data.meta.total_pages);
            setTotal(response.data.meta.total);
            showSnackbar('Patient list updated successfully', 'success');
        } else {
            showSnackbar('Error fetching updated patients', 'error');
        }
        setIsModalOpen(false);
    };

    const handleEditPatient = (patient) => {
        setCurrentPatient(patient);
        setIsModalOpen(true);
    };

    const handleDeletePatient = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('https://horizontclinic.com/horizont-be/deletePatient.php', { id: selectedPatient.id }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.status === 'success') {
                setPatients(patients.filter((p) => p.id !== selectedPatient.id));
                showSnackbar('Patient deleted successfully', 'success');
            } else {
                showSnackbar('Error deleting patient', 'error');
            }
            setIsDeleteModalOpen(false);
        } catch (error) {
            if (error?.response?.status === 401) {
                localStorage.removeItem('token');
                window.location.href = '/login';
                return;
            }
            console.error('Error deleting patient:', error);
            showSnackbar('An error occurred. Please try again.', 'error');
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleOpenDeleteModal = (patient) => {
        setSelectedPatient(patient);
        setIsDeleteModalOpen(true);
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handlePerPageChange = (e) => {
        setPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        setCurrentPage(1);
        // Debounce network search (optional simple approach)
        if (searchTypingTimer) clearTimeout(searchTypingTimer);
        const t = setTimeout(() => {
            // state change already triggers effect due to searchQuery dependency
        }, 300);
        setSearchTypingTimer(t);
    };

    // Server returns already filtered/paginated set.

    return (
        <div className="d-flex">
            <Sidebar />
            <div className="flex-grow-1">
                <TopNavbar />
                <div className={'container pt-5'}>
                    <div className="mb-4 px-3 d-flex justify-content-between align-items-center">
                        <h1>Patients</h1>
                        <Button variant="contained" color="primary" startIcon={<FaPlus />} onClick={handleAddPatient}>
                            Add Patient
                        </Button>
                    </div>
                    <div className="d-flex flex-wrap align-items-center justify-content-between ms-3 mb-3 gap-3">
                        <TextField
                            label="Search patient"
                            size="medium"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            style={{ minWidth: '320px', flexGrow: 1, maxWidth: '500px' }}
                        />
                        <div className="d-flex align-items-center gap-3">
                            <div className="text-muted small d-none d-md-block">Total: {total}</div>
                            <FormControl size="small" style={{ width: 140 }}>
                                <InputLabel id="per-page-label">Per Page</InputLabel>
                                <Select
                                    labelId="per-page-label"
                                    value={perPage}
                                    label="Per Page"
                                    onChange={handlePerPageChange}
                                >
                                    {[5,10,20,50].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </div>
                    </div>
                    
                    {/* Loading Spinner */}
                    {loading ? (
                        <div style={{ textAlign: 'center', margin: '20px' }}>
                            <CircularProgress />
                        </div>
                    ) : (
                        <>
                            <PatientsTable
                                patients={patients}
                                onEdit={handleEditPatient}
                                onDelete={handleOpenDeleteModal}
                            />

                            {/* Pagination Component */}
                            <div className="d-flex justify-content-center my-3">
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    showFirstButton
                                    showLastButton
                                />
                            </div>
                        </>
                    )}

                    <AddPatientModal
                        open={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onSubmit={handleSubmitPatient}
                        initialData={currentPatient || {}}
                        showSnackbar={showSnackbar}
                    />

                    <DeletePatientModal
                        open={isDeleteModalOpen}
                        onClose={() => setIsDeleteModalOpen(false)}
                        onConfirm={handleDeletePatient}
                        patient={selectedPatient}
                    />

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={3000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', backgroundColor: snackbar.severity === 'success' ? '#449998' : '#f44336' }}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </div>
            </div>
        </div>
    );
};

export default Patients;
